/**
 * User 타입 정의
 * 사용자 관리 관련 타입 및 변환 함수
 */

import { getPrmCodeLabel, getUseYnLabel, getUserTypeLabel } from '../../shared/config/system.config';
import { formatDateArray } from '../../shared/lib/dateFormatterHelpers';

// ======== DTO 타입 ========

/** 사용자 DTO (백엔드 응답) */
export type UserDto = {
    userId: string;
    userName: string;
    prmCode: string;
    userType: string | null; // A: 관리자, C: 본사, S: 협력사, R: 라이더
    phoneNum: string;
    emailInfo: string | null;
    useYn: string;
    suppType: string | null;
    suppName: string | null;
    insUser: string;
    insDate: number[];
    updUser: string | null;
    updDate: number[] | null;
};

/** 사용자 목록 조회 응답 DTO */
export type GetUserListResponseDto = {
    users: UserDto[];
    totalCount: number;
};

/** 사용자 생성 요청 DTO */
export type CreateUserRequestDto = {
    userId: string;
    userName: string;
    prmCode: string;
    phoneNum: string;
    emailInfo: string;
    useYn: string;
    suppType?: string;
    insertUserId?: string;
    password?: string;
};

/** 사용자 수정 요청 DTO */
export type UpdateUserRequestDto = {
    userId: string;
    userName?: string;
    prmCode?: string;
    phoneNum?: string;
    emailInfo?: string;
    useYn?: string;
    suppType?: string;
    updateUserId?: string;
};

// ======== Domain 타입 ========

/** 사용자 Domain Model */
export type User = {
    userId: string;
    userName: string;
    prmCode: string;
    prmCodeName: string;
    userType: string | null; // A: 관리자, C: 본사, S: 협력사, R: 라이더
    userTypeName: string;   // 사용자 타입 라벨
    phoneNum: string;
    emailInfo: string | null;
    useYn: string;
    useYnName: string;
    suppType: string | null;
    suppName: string | null;
    insUser: string;
    insDate: string;
    updUser: string | null;
    updDate: string | null;
};

// ======== 변환 함수 ========

/**
 * DTO -> Domain 변환
 * @param dto 백엔드 응답 DTO
 */
export const userFromDto = (dto: UserDto): User => ({
    userId: dto.userId,
    userName: dto.userName,
    prmCode: dto.prmCode,
    prmCodeName: getPrmCodeLabel(dto.prmCode),
    userType: dto.userType,
    userTypeName: getUserTypeLabel(dto.userType),
    phoneNum: dto.phoneNum,
    emailInfo: dto.emailInfo,
    useYn: dto.useYn,
    useYnName: getUseYnLabel(dto.useYn),
    suppType: dto.suppType,
    suppName: dto.suppName,
    insUser: dto.insUser,
    insDate: formatDateArray(dto.insDate),
    updUser: dto.updUser,
    updDate: formatDateArray(dto.updDate),
});

/**
 * 목록 DTO -> Domain 변환
 * @param response 목록 조회 응답
 */
export const userListFromDto = (response: GetUserListResponseDto): User[] => {
    if (!response?.users || !Array.isArray(response.users)) {
        return [];
    }
    return response.users.map(userFromDto);
};
